'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Logo } from '@/components/logo'
import { ThemeToggle } from '@/components/theme-toggle'
import { api } from '@/lib/api'

export function LoginForm() {
	const t = useTranslations('auth')
	const router = useRouter()

	const [password, setPassword] = useState('')
	const [pending, setPending] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const submit = async (event: React.FormEvent) => {
		event.preventDefault()
		if (pending) return

		setPending(true)
		setError(null)

		try {
			await api.login(password)
			router.replace('/')
			router.refresh()
		} catch {
			setError(t('invalid'))
			setPending(false)
		}
	}

	return (
		<div className="w-full max-w-sm">
			<div className="mb-8 flex items-center justify-between">
				<div className="flex items-center gap-3">
					<div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600">
						<Logo className="h-5 w-auto text-white" />
					</div>
					<span className="text-xl font-bold">{t('title')}</span>
				</div>
				<ThemeToggle />
			</div>

			<form onSubmit={submit} className="space-y-4 rounded-xl border bg-card p-6">
				<p className="text-sm text-muted-foreground">{t('subtitle')}</p>

				<Input
					type="password"
					autoFocus
					autoComplete="current-password"
					placeholder={t('passwordPlaceholder')}
					value={password}
					onChange={(event) => setPassword(event.target.value)}
					// 16px keeps iOS from zooming the page when the field takes focus.
					className="text-base"
				/>

				{error && <p className="text-sm text-destructive">{error}</p>}

				<Button type="submit" className="w-full" disabled={pending || password.length === 0}>
					{pending && <Loader2 className="h-4 w-4 animate-spin" />}
					{pending ? t('checking') : t('submit')}
				</Button>
			</form>
		</div>
	)
}
